import {BarDatum, BarTooltipProps, ResponsiveBar} from '@nivo/bar'
import {useEffect, useState} from "react";
import {basicFormat, currencyFormat} from "../utils";
import './BarChart.scss';
import {colors, organizationColors} from '../config';
import {NoData} from "./NoData";
import {SingleValue} from "./SingleValue";
import {OrganizationsSwitch} from "./OrganizationsSwitch";
import {CommonProps} from "./config";
import {Loader} from "../../shared/Loeader/Loader";

type BarChartProps = {
    data: Array<any>;
    groupBy: string;
    groupBy2?: string;
    activeFilters?: any;
    value: string;
    mainKey?: string;
    layout?: 'vertical' | 'horizontal';
    title?: string;
    valueFormat?: string;
    noSort?: boolean
    wide?: boolean
}
const BarChart = ({
                      data,
                      groupBy,
                      groupBy2,
                      activeFilters,
                      value,
                      layout = 'vertical',
                      title,
                      valueFormat,
                      wide,
                      mainKey = '',
                      noSort = false
                  }: BarChartProps) => {

    const [firstLoad, setFirstLoad] = useState(true);
    const [chartData, updateChartData] = useState<Array<BarDatum> | null>(null);
    const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
    const [isStacked, setIsStacked] = useState<boolean>(false);
    const [keys, updateKeys] = useState<Array<string>>([]);
    const [calculateWorker, setCalculateWorker] = useState<any>();

    const [localGroupBy, setLocaGroupBy] = useState(groupBy);

    const [isOrganizationChoosed, setIsOrganizationChoosed] = useState(false);

    useEffect(() => {
        if (activeFilters) {
            setIsOrganizationChoosed(activeFilters.findIndex((item: any) => item.key === "organization") !== -1);
        }
    }, [activeFilters])


    useEffect(() => {
        const newGroupBy = !groupBy2 ? groupBy : (
            isOrganizationChoosed ? groupBy2 : groupBy
        );
        setLocaGroupBy(newGroupBy);
    }, [isOrganizationChoosed]);

    useEffect(() => {
        setCalculateWorker(new Worker(window.location.origin + '/workers/bar-worker.js'));
    }, []);

    useEffect(() => {
        if (!calculateWorker) return;
        let isCancelled = false;
        calculateWorker.onmessage = ($event: MessageEvent) => {
            if ($event && $event.data && !isCancelled) {
                updateChartData($event.data.barData);
                updateKeys($event.data.newKeys);
            }
        };
        return () => {
            isCancelled = true;
            calculateWorker.terminate();
        };
    }, [calculateWorker]);


    useEffect(() => {
        if (!firstLoad) {
            setIsDataLoading(false);
        }
    }, [chartData]);

    useEffect(() => {
        if (firstLoad && data.length !== 0) setFirstLoad(false);
        if (!calculateWorker) return;
        setIsDataLoading(true);

        calculateWorker.postMessage({
            data,
            isStacked: mainKey && isStacked,
            mainKey,
            groupBy: localGroupBy,
            noSort,
            value
        })

    }, [data, isStacked]);


    const tooltip = ({id, value, color}: BarTooltipProps<any>) => {
        const style = {
            padding: 12,
            color,
            minWidth: '120px',
            maxWidth: '300px',
            background: '#222222',
            borderRadius: '4px',
        };

        const strongStyle = {
            marginLeft: '5px',
        };

        return (
            <div style={style}>
                <span style={{}}>{id}:</span>
                <strong style={strongStyle}>
                    {valueFormat === 'currency' ? currencyFormat(value) : basicFormat(value)}
                </strong>
            </div>
        );
    }

    const axisBottom = {
        tickValues: 5,
        tickSize: 6,
        tickPadding: 8,
        tickRotation: -35,
        format: (layout === 'horizontal' && valueFormat === 'currency') ? currencyFormat : (v: any) => {
            return v && v.length > 17 ? (
                <tspan>
                    {v.substring(0, 17) + "..."}
                    <title>{v}</title>
                </tspan>
            ) : (v);
        },
    };

    const axisLeft = {
        tickValues: 6,
        format: (layout === 'vertical' && valueFormat === 'currency') ? currencyFormat : (value: any) => value
    };

    const Bar = () => (
        chartData && <div className="responsiveBar__container">
            <ResponsiveBar
                {...CommonProps}
                data={chartData}
                keys={keys}
                valueFormat={valueFormat === 'currency' ? currencyFormat : basicFormat}
                indexBy={localGroupBy}
                enableGridX={layout === 'horizontal'}
                enableGridY={layout === 'vertical'}
                colors={((mainKey === 'organization' && isStacked) || localGroupBy === 'organization') ? ({id}) => {
                    // @ts-ignore
                    return organizationColors[id] as string;
                } : colors}
                layout={layout}
                tooltip={tooltip}
                motionConfig="gentle"
                axisBottom={axisBottom}
                axisLeft={axisLeft}
            />
        </div>
    )

    return (
        <div className={'chart'} style={ wide ? {
            width: '100%'
        }: {}}>
            <h3>{title || groupBy}</h3>
            {(mainKey && data && chartData && chartData.length >= 2) && <OrganizationsSwitch isStacked={isStacked}
                                                                                             setIsStacked={setIsStacked}/>}
            <div style={layout === 'vertical' ? {height: 400} : {height: 700 - 12}}>
                {(isDataLoading) ?
                    <div style={{overflow: 'hidden'}}>
                        <Loader/>
                    </div> : (chartData && (chartData.length === 0 || chartData.findIndex((item:any) => item.value !== 0) === -1)
                        ? <NoData/>
                        : (chartData && (chartData.length === 1) ?
                            <SingleValue value={chartData && chartData[0]} format={valueFormat}
                                         groupBy={localGroupBy}/> : Bar()))}
            </div>
        </div>
    )
}

export {BarChart}